import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { supabase, isAuthConfigured } from '@/lib/supabase'
import {
  clearMcpOAuthPendingParams,
  loadMcpOAuthPendingParams,
  saveMcpOAuthPendingParams,
  type McpOAuthPendingParams,
} from '@/lib/auth-redirect'
import { BRAND } from '@/lib/config/brand'
import { testIds } from '@/lib/test-ids'
import { Button } from '@/components/ui/button'

type BridgePhase = 'loading' | 'needs_login' | 'completing' | 'success' | 'error'

const FINISH_TIMEOUT_MS = 45_000

/** Custom protocol redirects (cursor://, vscode://) hand off to the app but rarely unload the tab. */
function redirectToOAuthClient(redirectUrl: string) {
  try {
    const anchor = document.createElement('a')
    anchor.href = redirectUrl
    anchor.rel = 'noopener'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } catch {
    window.location.href = redirectUrl
  }
}

function readParamsFromSearch(searchParams: URLSearchParams): McpOAuthPendingParams | null {
  const client_id = searchParams.get('client_id')
  const redirect_uri = searchParams.get('redirect_uri')
  const state = searchParams.get('state')
  const code_challenge = searchParams.get('code_challenge')
  const code_challenge_method = searchParams.get('code_challenge_method')
  const scope = searchParams.get('scope')

  if (
    !client_id ||
    !redirect_uri ||
    !state ||
    !code_challenge ||
    !code_challenge_method
  ) {
    return null
  }

  return {
    client_id,
    redirect_uri,
    state,
    code_challenge,
    code_challenge_method,
    scope: scope ?? '',
  }
}

/**
 * Browser step for hosted MCP OAuth: completes PKCE after Cursor opens /oauth/authorize.
 * Public route (not behind ProtectedLayout) so login can preserve OAuth params.
 * Requires VITE_HOSTED_MCP_ORIGIN (public MCP HTTPS origin, no /mcp path).
 */
export function McpOAuthBridgePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [phase, setPhase] = useState<BridgePhase>('loading')
  const [error, setError] = useState<string | null>(null)
  const [closeAttempted, setCloseAttempted] = useState(false)
  const finishStarted = useRef(false)
  const clientRedirectUrl = useRef<string | null>(null)

  const resolveParams = useCallback((): McpOAuthPendingParams | null => {
    const fromUrl = readParamsFromSearch(searchParams)
    if (fromUrl) {
      saveMcpOAuthPendingParams(fromUrl)
      return fromUrl
    }
    return loadMcpOAuthPendingParams()
  }, [searchParams])

  const finishOAuth = useCallback(async (params: McpOAuthPendingParams) => {
    const mcpOrigin = import.meta.env.VITE_HOSTED_MCP_ORIGIN?.replace(/\/$/, '') ?? ''

    if (!isAuthConfigured || !supabase) {
      setPhase('error')
      setError('Sign-in is not configured for this deployment.')
      return
    }
    if (!mcpOrigin) {
      setPhase('error')
      setError(
        'VITE_HOSTED_MCP_ORIGIN is not set. Add the hosted MCP HTTPS origin to the frontend env.'
      )
      return
    }

    const { data: sessionResult } = await supabase.auth.getSession()
    const session = sessionResult.session
    if (!session) {
      saveMcpOAuthPendingParams(params)
      setPhase('needs_login')
      const next = `/mcp-oauth?${new URLSearchParams({
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        state: params.state,
        code_challenge: params.code_challenge,
        code_challenge_method: params.code_challenge_method,
        scope: params.scope,
      }).toString()}`
      navigate(`/login?redirect=${encodeURIComponent(next)}`, { replace: true })
      return
    }

    setPhase('completing')

    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), FINISH_TIMEOUT_MS)

    let r: Response
    try {
      r = await fetch(`${mcpOrigin}/oauth/finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          session_access_token: session.access_token,
          client_id: params.client_id,
          redirect_uri: params.redirect_uri,
          state: params.state,
          code_challenge: params.code_challenge,
          code_challenge_method: params.code_challenge_method,
          scope: params.scope,
        }),
      })
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setPhase('error')
        setError(
          'Connection timed out - Maater database may be busy. Wait a minute and retry - do not reconnect MCP from scratch.'
        )
        return
      }
      throw err
    } finally {
      window.clearTimeout(timeoutId)
    }

    const j = (await r.json().catch(() => ({}))) as { error?: string; redirect_url?: string }
    if (!r.ok) {
      setPhase('error')
      if (r.status === 503 || j.error === 'database_unavailable') {
        setError(
          'Maater database is busy. Wait a minute and retry - do not reconnect MCP from scratch.'
        )
      } else {
        setError(typeof j.error === 'string' ? j.error : 'OAuth bridge failed')
      }
      return
    }
    if (!j.redirect_url) {
      setPhase('error')
      setError('Invalid response from MCP server')
      return
    }

    clearMcpOAuthPendingParams()
    clientRedirectUrl.current = j.redirect_url
    setPhase('success')
    redirectToOAuthClient(j.redirect_url)
    window.setTimeout(() => {
      setCloseAttempted(true)
      try {
        window.close()
      } catch {
        /* Browsers block close() unless this tab was script-opened */
      }
    }, 400)
  }, [navigate])

  useEffect(() => {
    if (finishStarted.current) return
    finishStarted.current = true

    const params = resolveParams()
    if (!params) {
      setPhase('error')
      setError('Missing OAuth parameters. Start the connection from Cursor MCP settings.')
      return
    }

    finishOAuth(params).catch(() => {
      setPhase('error')
      setError(
        'Unexpected error completing MCP sign-in - Maater database may be busy. Wait a minute and retry.'
      )
    })
  }, [finishOAuth, resolveParams])

  const retry = () => {
    finishStarted.current = false
    clientRedirectUrl.current = null
    setCloseAttempted(false)
    setError(null)
    setPhase('loading')
    const params = resolveParams()
    if (!params) {
      setPhase('error')
      setError('Missing OAuth parameters. Start the connection from Cursor MCP settings.')
      return
    }
    finishStarted.current = true
    finishOAuth(params).catch(() => {
      setPhase('error')
      setError(
        'Unexpected error completing MCP sign-in - Maater database may be busy. Wait a minute and retry.'
      )
    })
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center p-6"
      data-testid={testIds.oauth.mcpOAuthBridge}
    >
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-lg font-semibold">Connect Cursor to {BRAND.displayName}</h1>
        {phase === 'needs_login' ? (
          <p className="text-sm text-muted-foreground">Redirecting to sign in...</p>
        ) : phase === 'completing' || phase === 'loading' ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {phase === 'loading' ? 'Preparing connection...' : 'Completing sign-in...'}
            </p>
            <p className="text-xs text-muted-foreground">
              Return to Cursor when this tab closes or shows a success message.
            </p>
          </div>
        ) : phase === 'success' ? (
          <div
            className="flex flex-col items-center gap-3"
            data-testid={testIds.oauth.mcpOAuthSuccess}
          >
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="text-sm font-medium">Connected to {BRAND.displayName} MCP</p>
            <p className="text-sm text-muted-foreground max-w-sm">
              Authorization was sent to Cursor. Return to Cursor MCP settings - the connection
              should show as active. This tab is safe to close.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={() => window.close()}>
                Close tab
              </Button>
              {clientRedirectUrl.current && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => redirectToOAuthClient(clientRedirectUrl.current!)}
                >
                  Send to Cursor again
                </Button>
              )}
            </div>
            {closeAttempted && (
              <p className="text-xs text-muted-foreground max-w-sm">
                If Cursor still shows &quot;Waiting for callback&quot;, click Send to Cursor again,
                then check MCP settings.
              </p>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-destructive">{error}</p>
            <Button type="button" variant="outline" onClick={retry}>
              Try again
            </Button>
            <p className="text-xs text-muted-foreground">
              Or disconnect and reconnect MCP in Cursor settings to start a fresh authorization.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
